import { ChangeDetectionStrategy, Component, DestroyRef, ErrorHandler, input, output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { DirectsState, UserState } from '@coin/shared/auth/feature';
import { catchError, EMPTY, mergeMap, Observable } from 'rxjs';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { SimplebarAngularModule } from 'simplebar-angular';
import { KeyboardMenuNavigationDirective } from '@coin/shared/core/util-directives';
import { DirectsOverviewComponent } from '../directs-overview/directs-overview.component';
import { DirectItemOverviewComponent } from './parts/direct-item-overview/direct-item-overview.component';

@Component({
  selector: 'customer-app-directs',
  templateUrl: './directs.component.html',
  styleUrls: ['./directs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DirectItemOverviewComponent,
    V2ButtonComponent,
    MatTabsModule,
    TranslateModule,
    SimplebarAngularModule,
    KeyboardMenuNavigationDirective
  ]
})
export class DirectsComponent {
  private readonly dialog = inject(MatDialog);
  private readonly userState = inject(UserState);
  private readonly directsState = inject(DirectsState);
  private readonly errorHandler = inject(ErrorHandler);

  isActive = input(false);
  // eslint-disable-next-line @angular-eslint/no-output-native
  toggle = output();
  closeMenu = output();

  user$ = this.userState.user$;
  inCompanyManagerDirects = this.directsState.inCompanyManagerDirects;
  lineManagerDirects = this.directsState.lineManagerDirects;

  constructor() {
    const destroyRef = inject(DestroyRef);

    this.user$
      .pipe(
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil -- ngxs compatibility: started calls must finish like dispatched actions
        takeUntilDestroyed(destroyRef),
        mergeMap(coinUser => {
          if (coinUser?.id) {
            return this.directsState.getDirects(coinUser.id).pipe(catchError(error => this.reportError(error)));
          }

          this.directsState.clearDirects();
          return EMPTY;
        })
      )
      .subscribe();
  }

  public openDirectsDialog(): void {
    this.dialog.open(DirectsOverviewComponent, {
      disableClose: true,
      width: '80vw',
      minHeight: '75%',
      maxHeight: '80vh',
      maxWidth: '1000px',
      panelClass: 'close-icon-and-transition',
      data: { lineManager: this.lineManagerDirects(), inCompanyManager: this.inCompanyManagerDirects() }
    });
  }

  private reportError(error: unknown): Observable<never> {
    this.errorHandler.handleError(error);
    return EMPTY;
  }
}
